import type { CSSProperties } from 'react';
import { useProgress } from '../state/Progress';
import { vars } from '../styles/theme.css';

/**
 * Subtle status indicator next to a lesson card on the lessons list.
 *
 *   none      → renders nothing
 *   partial   → empty ring in the accent colour
 *   complete  → filled accent dot
 */

type Props = {
  lessonId: string;
  totalSections: number;
  style?: CSSProperties;
};

const SIZE = 8;

export function LessonProgressDot({ lessonId, totalSections, style }: Props) {
  const { lessonStatus } = useProgress();
  const status = lessonStatus(lessonId, totalSections);

  if (status === 'none') {
    // Reserve the same width so the row layout doesn't shift when sections
    // get checked.
    return (
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          width: SIZE,
          height: SIZE,
          flexShrink: 0,
          ...style,
        }}
      />
    );
  }

  const isComplete = status === 'complete';
  return (
    <span
      role="status"
      aria-label={isComplete ? 'Complete' : 'In progress'}
      title={isComplete ? 'Complete' : 'In progress'}
      style={{
        display: 'inline-block',
        width: SIZE,
        height: SIZE,
        borderRadius: SIZE / 2,
        flexShrink: 0,
        backgroundColor: isComplete ? vars.color.accent : 'transparent',
        border: `1.5px solid ${vars.color.accent}`,
        ...style,
      }}
    />
  );
}
